import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import Edit16 from '@carbon/icons-react/es/edit/16';
import Link16 from '@carbon/icons-react/es/link/16';
import { Layout, Typography } from 'antd';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import useClipboard from 'react-use-clipboard';
import { ProjectConversationId, ProjectId } from 'api/models';
import { ProjectConversation } from 'api/project-conversations';
import { Menu } from 'components/atoms/dropdown';
import { MoreActionsDropdown } from 'components/atoms/more-actions';
import { toastService } from 'components/molecules/toast';
import { PermissionRuleName } from 'constant';
import { usePermissionForRule } from 'hooks/permissions/use-permission';
import { useDeleteConfirmModal } from 'hooks/use-delete-confirm-modal';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import {
  useDeleteProjectConversationsMutation,
  useRemoveMembersProjectConversationsMutation,
} from 'queries/project-wide-conversation';
import { getProjectConversationPageUrl } from 'utils/urls';
import { useActiveProjectConversation } from '../../proj-conversation-hooks';
import { ChannelMembers } from '../channel-members';
import { UpdateConversationModal } from '../conversation-form';
import { ProjectConversationPane } from './conversation-pane';
import {
  ChannelDescription,
  ChannelName,
  ChannelNameDescriptionWrapper,
  Content,
  HeaderContent,
  HeaderContentPart,
} from './styles';
import styles from './styles.module.css';

interface Props {
  projectId: ProjectId;
}

const menu = ({
  onCopy,
  onEditDetails,
  onDelete,
  onLeave,
  activeConversation,
  isUserMemberOfChannel,
  hasDeleteChatAccess,
}: {
  onCopy: () => void;
  onDelete: () => void;
  onEditDetails: () => void;
  onLeave: () => void;
  activeConversation?: ProjectConversation;
  isUserMemberOfChannel?: boolean;
  hasDeleteChatAccess: boolean;
}) => (
  <Menu
    data-test-id="project-conversation.menu"
    data-template-id={activeConversation?.conversationId}
  >
    <Menu.Header>
      <FormattedMessage id="more_actions" />
    </Menu.Header>
    <Menu.Item
      data-test-id="project-conversation.menu.copy"
      onClick={onCopy}
      key="copy"
      icon={<Link16 />}
    >
      <FormattedMessage id="projects.conversations.copy_link" />
    </Menu.Item>
    <Menu.ItemWithPermission
      data-test-id="project-conversation.menu.copy"
      onClick={onEditDetails}
      key="edit"
      icon={<Edit16 />}
      ruleName={PermissionRuleName.ProjectChatEditAction}
    >
      <FormattedMessage id="projects.conversations.edit_details" />
    </Menu.ItemWithPermission>
    {hasDeleteChatAccess || isUserMemberOfChannel ? <Menu.Divider /> : null}
    {isUserMemberOfChannel ? (
      <Menu.Item
        data-test-id="project-conversation.menu.leave"
        onClick={onLeave}
        key="leave"
        icon={<ArrowRight16 />}
      >
        <FormattedMessage id="projects.conversations.members.leave" />
      </Menu.Item>
    ) : null}
    {hasDeleteChatAccess ? (
      <Menu.ItemDelete
        data-test-id="project-conversation.menu.delete"
        key="delete"
        onClick={onDelete}
      >
        <FormattedMessage id="projects.conversations.delete" />
      </Menu.ItemDelete>
    ) : null}
  </Menu>
);

export const ProjectConversationPaneWrapper: FC<Props> = ({ projectId }) => {
  const activeConversation = useActiveProjectConversation();
  const [isUpdateDetailsModalVisible, setUpdateConversationModalVisibility] =
    useState(false);
  const {
    user: { userId },
  } = useUser();
  const history = useHistory();
  const isUserMemberOfChannel =
    activeConversation && activeConversation.partOfConversation;
  const [, copyToClipboard] = useClipboard(window.location.href);

  const message = useTranslation('copied');
  const description = useTranslation(
    'projects.conversations.copied_description',
  );

  const toast = toastService({
    title: message,
    message: description,
  });

  const onCopy = () => {
    copyToClipboard();
    toast.success();
  };

  const onEditDetails = () => {
    setUpdateConversationModalVisibility(true);
  };

  const { mutateAsync: muatateDeleteConversation } =
    useDeleteProjectConversationsMutation(projectId);

  const onDelete = () => {
    if (activeConversation?.conversationId) {
      return muatateDeleteConversation(
        ProjectConversationId(activeConversation.conversationId),
        {
          onSuccess: () => {
            history.push(getProjectConversationPageUrl(projectId));
          },
        },
      );
    }

    return Promise.resolve();
  };

  const { mutateAsync: muatateLeaveConversation } =
    useRemoveMembersProjectConversationsMutation(projectId);

  const onLeave = () => {
    if (activeConversation?.conversationId) {
      return muatateLeaveConversation({
        users: [
          {
            userId,
          },
        ],
        conversationId: ProjectConversationId(
          activeConversation.conversationId,
        ),
      });
    }

    return Promise.resolve();
  };

  const deleteTitle = useTranslation(
    'projects.conversations.delete.confirm_title',
  );
  const deleteConfirmationText = useTranslation(
    'projects.conversations.delete.confirm_info',
  );
  const deleteConfirmation = useDeleteConfirmModal({
    title: deleteTitle,
    content: deleteConfirmationText,
    onOk: onDelete,
  });

  const leaveTitle = useTranslation('projects.conversations.members.leave');
  const leaveConfirmationText = useTranslation(
    'projects.conversations.leave.confirm_info',
  );
  const leaveConfirmation = useDeleteConfirmModal({
    title: leaveTitle,
    content: leaveConfirmationText,
    okText: leaveTitle,
    onOk: onLeave,
  });
  const { isAllowedForCurrentUser } = usePermissionForRule();
  const hasDeleteChatAccess = isAllowedForCurrentUser(
    PermissionRuleName.ProjectChatDeleteAction,
  );

  return (
    <Layout.Content>
      <Content>
        <HeaderContent private={activeConversation?.private}>
          <HeaderContentPart>
            <ChannelMembers projectId={projectId} />
            <ChannelNameDescriptionWrapper>
              <ChannelName>{activeConversation?.conversationName}</ChannelName>
              {activeConversation?.conversationDescription && (
                <ChannelDescription>
                  <Typography.Text ellipsis={{ tooltip: true }}>
                    {activeConversation?.conversationDescription}
                  </Typography.Text>
                </ChannelDescription>
              )}
            </ChannelNameDescriptionWrapper>
          </HeaderContentPart>
          <HeaderContentPart>
            <UpdateConversationModal
              isVisible={isUpdateDetailsModalVisible}
              onCancel={() => setUpdateConversationModalVisibility(false)}
            />
            <MoreActionsDropdown
              overlayClassName={styles.conv_pane__more_actions}
              menu={menu({
                onCopy,
                onEditDetails,
                onDelete: deleteConfirmation,
                onLeave: leaveConfirmation,
                activeConversation,
                isUserMemberOfChannel,
                hasDeleteChatAccess,
              })}
            />
          </HeaderContentPart>
        </HeaderContent>
        <ProjectConversationPane
          projectId={projectId}
          isUserMemberOfChannel={isUserMemberOfChannel}
        />
      </Content>
    </Layout.Content>
  );
};
